import { Pagination, Spin, Table } from 'pink-lava-ui';
import type { ApiListParams, ApiListResponse } from '@dc-trans/data-layer';
import type { ColumnType } from 'antd/es/table';
import { Pagination as AntdPagination } from 'antd';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useEffect, useState, type ReactNode } from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import EmptyPlaceholder from '@/components/ui/EmptyPlaceholder';
import type { UseQueryResult } from '@tanstack/react-query';
import type { sizeType } from 'pink-lava-ui/types';
import type { FilterFieldType } from '@/components/ui/form/FilterBuilder';

interface ListProps<T extends Record<string, any> = any> {
  columns?: ColumnType<T>[];
  useQuery: (_params?: ApiListParams<any>, _queryKey?: any[]) => UseQueryResult<ApiListResponse<T>, Error>;
  queryKey?: any[];
  defaultPageSize?: number;
  tableSize?: sizeType;
  title?: string;
  noWrapper?: boolean;
  emptyDesc?: string | boolean;
  renderItem?: (_item: T, _index: number) => ReactNode;
  loadingBufferTime?: number;
  defaultFilters?: Record<string, any>;
  defaultSort?: { sortBy: string; sortOrder: 'asc' | 'desc' };
  /** Filter fields configuration to determine which fields should not be split */
  filterFields?: FilterFieldType<any>[];
}

const List = <T extends Record<string, any> = any>({
  columns,
  useQuery,
  queryKey = [],
  title,
  emptyDesc = 'Coba gunakan filter lainnya.',
  noWrapper = false,
  loadingBufferTime = 500,
  tableSize,
  renderItem,
  defaultPageSize = 20,
  defaultFilters,
  defaultSort,
  filterFields,
}: ListProps<T>) => {
  const { isMobile } = useBreakpoint();
  const { updateParam, params, updateUrlParams } = useUrlParamsContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingBuffer, setLoadingBuffer] = useState(false);

  const { page, limit, ...filters } = params;

  const processedFilters = Object.entries(filters).reduce(
    (acc, [key, value]) => {
      // Check if this field is a search-type field
      const isSearchField = filterFields?.some(field => {
        if (field.type === 'search') {
          if (field.name === key) return true;
          if (field.searchOptions?.some(option => option.name === key)) return true;
        }
        return false;
      });

      // Only split if NOT a search field AND contains comma
      if (typeof value === 'string' && value.includes(',') && !isSearchField) {
        acc[key] = value.split(',');
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>
  );

  const { data, isLoading, isFetching } = useQuery(
    {
      page,
      limit,
      filters: {
        ...defaultFilters,
        ...(defaultSort ? { sort_by: defaultSort.sortBy, sort_order: defaultSort.sortOrder } : {}),
        ...processedFilters,
      },
    },
    queryKey
  );

  useEffect(() => {
    if (!isInitialized) {
      updateUrlParams({
        ...params,
        page: String(params.page || 1),
        limit: String(params.limit || defaultPageSize),
      });
      setIsInitialized(true);
    }
  }, [isInitialized, updateUrlParams, params, defaultPageSize]);

  useEffect(() => {
    setTimeout(() => {
      setLoadingBuffer(true);
    }, loadingBufferTime);
  }, [loadingBufferTime]);

  const handlePageChange = (page: number) => {
    // Update URL params
    updateParam('page', page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updateUrlParams({
      ...params,
      limit: newPageSize,
      page: 1,
    });
  };

  const listData = (data?.data?.items || []) as readonly T[];
  const total = data?.data?.meta?.total || 0;

  // Show loading when fetching data to prevent flashing
  const shouldShowLoading = isLoading || isFetching || !loadingBuffer;

  return (
    <div className="flex-1 h-full flex flex-col m-4 mt-0 mb-0">
      {shouldShowLoading && (
        <div className="flex flex-1 w-full items-center flex-col justify-center h-full">
          <Spin size={'large'} />
        </div>
      )}
      {!shouldShowLoading && listData.length === 0 && (
        <div className="flex flex-1 w-full items-center flex-col justify-center h-full">
          <EmptyPlaceholder title={title} description={emptyDesc} />
        </div>
      )}
      {!shouldShowLoading && renderItem && listData.length > 0 && (
        <>
          <div className="w-full flex-1 flex flex-col gap-2">
            {listData.map((item, index) => renderItem(item, index))}
          </div>
        </>
      )}
      {!shouldShowLoading && columns && listData.length > 0 && (
        <div className={noWrapper ? undefined : 'bg-white rounded-2xl p-4 flex-1 shadow-md'}>
          <Table
            columns={columns}
            dataSource={listData}
            loading={false}
            size={tableSize}
            pagination={false}
            rowKey="ulid"
            scroll={{
              x: 1000,
            }}
          />
        </div>
      )}
      <div className={noWrapper ? undefined : 'pb-4'}>
        <div className={noWrapper ? undefined : 'bg-white py-2 px-3 rounded-2xl mt-4 shadow-md'}>
          {isMobile && (
            <AntdPagination
              total={total}
              onChange={handlePageChange}
              pageSize={limit}
              defaultCurrent={page}
              showSizeChanger
              onShowSizeChange={handlePageSizeChange}
              align="center"
            />
          )}
          {!isMobile && (
            <Pagination
              totalItems={total}
              onChange={handlePageChange}
              page={page}
              itemsPerPage={limit}
              totalPages={Math.ceil(total / limit!)}
              setItemsPerPage={handlePageSizeChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default List;
export type { ListProps };
